<template>
  <div class="single-image-uploader-container" @paste="onPaste">
    <label class="siu-label" v-if="label != null && label != ''" tabindex="1">{{ label }}</label>
    <div class="single-image-uploader" v-if="id != null && apiUrl != null">
      <input
        type="file"
        :id="inputId"
        accept=".png, .jpg, .jpeg"
        hidden
        @change="onFileInputChange"
        :disabled="disabled" />
      <div
        class="siu-dropzone"
        @dragenter="onDragEnter"
        :class="uploadingError ? 'uploading-error' : ''">
        <div
          class="siu-drop-catcher"
          @dragleave="onDragLeave"
          @dragover.prevent
          @drop="onDrop"
          v-if="uploadAllowed && dropZoneIsOnDrag && !uploadingActive">
          <v-icon x-large class="mb-3">mdi-tray-arrow-down</v-icon>
          <span>Drop Image Here</span>
        </div>
        <div class="siu-upload-wrapper">
          <div class="siu-empty-placeholder" v-if="emptyMode">
            <p class="siu-icon">
              <v-icon x-large>mdi-image-outline</v-icon>
            </p>
            <p class="siu-main-info m-0" v-if="!disabled">
              Drop your image here, or
              <label :for="inputId" v-show="false" :id="'BrowseLabel_' + inputId"> browse </label>
              <v-btn
                small
                text
                color="primary"
                elevation="0"
                @click="clickOnLabel('BrowseLabel_' + inputId)">
                <v-icon left>mdi-arrow-collapse-up</v-icon>browse
              </v-btn>
            </p>
            <p class="siu-main-info mb-0" v-else>No Image Available!</p>
            <p class="siu-support" v-if="!disabled">Supports: JPG, PNG</p>
          </div>
          <div class="siu-queue" v-if="uploadAllowed && uploadingMode">
            <div class="image-uploader">
              <v-badge
                overlap
                :color="uploadingError ? 'error' : 'blue'"
                dark
                left>
                <template v-slot:badge>
                  <v-tooltip top z-index="999" v-if="uploadingError">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small v-bind="attrs" v-on="on">mdi-exclamation</v-icon>
                    </template>
                    <span>{{ uploadingErrorMessage || 'Upload failed' }}</span>
                  </v-tooltip>
                  <v-icon v-else small class="fa-spin">mdi-autorenew</v-icon>
                </template>
                <img class="image-thumb" :src="fakeImageUrl" />
              </v-badge>
              <div class="image-info">
                <div class="image-name" v-text="imageToUploadName"></div>
                <div class="image-size" v-text="imageToUploadSize"></div>
                <div class="image-actions">
                  <v-btn icon elevation="0" v-if="uploadingError" @click="retry()" color="error">
                    <v-icon>mdi-redo</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    elevation="0"
                    v-if="!uploadingActive"
                    @click="deleteNewImage()"
                    color="error">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                  <v-btn icon elevation="0" v-if="uploadingActive" @click="cancelUpload()">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div class="uploading-progress">
              <div class="bar" v-show="!uploadingError" :style="{ width: uploadingPCT }"></div>
              <span class="percentage" v-show="!uploadingError" v-text="uploadingPCT"></span>
            </div>
          </div>
          <div class="siu-result" v-if="imageIsValid && !uploadingMode && !emptyMode">
            <div class="image-viewer">
              <v-badge overlap color="green" dark left icon="mdi-check">
                <v-tooltip right z-index="999" nudge-right="-4px">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <div>
                        <img class="image-thumb" :key="activeImageUrl" :src="activeImageUrl" />
                      </div>
                    </div>
                  </template>
                  <span>
                    <v-img :src="activeImageUrl" height="250px" width="250px" contain></v-img>
                  </span>
                </v-tooltip>
              </v-badge>
              <div class="image-actions">
                <v-btn small text elevation="0" @click="deleteValidImage()" v-if="!disabled">
                  <v-icon class="mr-2">mdi-trash-can-outline</v-icon>
                  Remove
                </v-btn>
                <label
                  v-if="!disabled"
                  class="v-btn v-btn--text theme--light elevation-0 v-size--small"
                  :for="inputId"
                  v-show="false"
                  :id="'ChangeLabel_' + inputId">
                  <v-icon class="mr-2">mdi-arrow-collapse-up</v-icon>Change
                </label>
                <v-btn
                  small
                  text
                  elevation="0"
                  @click="clickOnLabel('ChangeLabel_' + inputId)"
                  v-if="!disabled">
                  <v-icon class="mr-2">mdi-arrow-collapse-up</v-icon>
                  Change
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import httpService from '@/utils/httpService';
  import { log, warn, error } from '@/utils/debugLogger';
  export default {
    name: 'single-image-uploader',
    data() {
      return {
        imageToUpload: null,
        imageIsValid: false,
        cancellation: null,
        uploadingMode: false,
        uploadingActive: false,
        uploadingError: false,
        uploadingErrorMessage: '',
        uploadingPCT: '0%',
        emptyMode: true,
        fakeImageUrl: null,
        activeImageUrl: null,
        dropZoneIsOnDrag: false,
      };
    },
    props: {
      id: {
        type: String,
        default: null,
      },
      apiUrl: {
        type: String,
        default: null,
      },
      value: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: null,
      },
    },
    beforeDestroy() {},
    mounted() {
      if (this.value != null && this.value != '') {
        this.emptyMode = false;
        this.imageIsValid = true;
        this.activeImageUrl = this.value;
      }
    },
    computed: {
      inputId() {
        return 'siu-input-' + this.id;
      },
      uploadAllowed() {
        if (!this.disabled) return true;
        return false;
      },
    },
    watch: {
      value: {
        handler() {
          this.activeImageUrl = this.value;
          if (this.value != null && this.value != '') {
            this.emptyMode = false;
            this.imageIsValid = true;
          } else {
            this.emptyMode = true;
            this.imageIsValid = false;
          }
        },
        deep: true,
      },
    },
    methods: {
      clickOnLabel(labelId) {
        document.getElementById(labelId).click();
      },
      onPaste(evt) {
        log(`Handle the paste event`, evt.clipboardData);

        // Get the data of clipboard
        const clipboardItems = evt.clipboardData.items;
        const items = [].slice.call(clipboardItems).filter(function (item) {
          // Filter the image items only
          return item.type.indexOf('image') !== -1;
        });
        if (items.length === 0) {
          return;
        }

        const item = items[0];
        // Get the blob of image
        const blob = item.getAsFile();
        this.pastedFromClipboard(blob);
      },
      pastedFromClipboard(imageBlob) {
        if (this.disabled || this.uploadingActive) return;
        this.processNewImage(imageBlob);
      },
      onDragEnter() {
        if (this.uploadingActive) return;
        this.dropZoneIsOnDrag = true;
      },
      onDragLeave() {
        if (this.uploadingActive) return;
        this.dropZoneIsOnDrag = false;
      },
      onDrop(event) {
        if (this.uploadingActive) return;
        this.dropZoneIsOnDrag = false;
        if (this.uploadingActive) {
          return; //don't allow adding new files if files already exist
        }
        event.preventDefault();
        event.stopPropagation();
        var images = event.target.files || event.dataTransfer.files;
        log('droped:', images);
        if (images.length) {
          this.processDroppedImages(images);
        }
      },
      processDroppedImages(images) {
        this.processNewImage(images[0]);
      },
      cancelUpload() {
        this.cancellation.abort();
      },
      deleteValidImage() {
        this.emptyMode = true;

        this.uploadingMode = false;
        this.uploadingActive = false;
        this.uploadingError = false;
        this.uploadingErrorMessage = '';

        this.imageToUpload = null;
        this.fakeImageUrl = null;

        this.imageIsValid = false;
        this.activeImageUrl = null;
        this.annouceImageUpdate();
      },
      annouceImageUpdate() {
        this.$emit('input', this.activeImageUrl);
      },
      deleteNewImage() {
        this._deleteNewRestoreOld();
      },
      _deleteNewRestoreOld() {
        if (!this.imageIsValid || this.activeImageUrl == null) {
          this.deleteValidImage();
        } else {
          this.emptyMode = false;

          this.uploadingMode = false;
          this.uploadingActive = false;
          this.uploadingError = false;
          this.uploadingErrorMessage = '';

          this.imageToUpload = null;
          this.fakeImageUrl = null;
        }
      },
      retry() {
        this.upload();
      },
      humanFileSize(bytes, si = false) {
        var thresh = si ? 1000 : 1024;
        if (Math.abs(bytes) < thresh) {
          return bytes + ' B';
        }
        var units = si
          ? ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
          : ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        var u = -1;
        do {
          bytes /= thresh;
          ++u;
        } while (Math.abs(bytes) >= thresh && u < units.length - 1);
        return bytes.toFixed(1) + ' ' + units[u];
      },
      acceptableImageFile(imageFile) {
        // Note: SVG removed for security (can contain executable JavaScript)
        if (
          imageFile.type == 'image/jpeg' ||
          imageFile.type == 'image/png'
        ) {
          return true;
        } else return false;
      },
      onFileInputChange(event) {
        if (this.imageToUpload) {
          return; //don't allow adding new images if there's already one
        }
        var images = Array.from(event.target.files);
        log('onChange', event, images);
        this.processNewImage(images[0]);
      },
      processNewImage(image) {
        if (this.acceptableImageFile(image)) {
          log('acceptableImageFile', image);
          this.imageToUpload = image;
          this.imageToUploadName = this.imageToUpload.name;
          this.imageToUploadSize = this.humanFileSize(this.imageToUpload.size);
          this.fakeImageUrl = URL.createObjectURL(this.imageToUpload);
          this.cancellation = new AbortController();

          this.upload();
        }
      },
      upload() {
        log('upload()', this.imageToUpload.name);
        var formData = new FormData();
        formData.append('image', this.imageToUpload, this.imageToUpload.name);

        this.emptyMode = false;
        this.uploadingMode = true;
        this.uploadingActive = true;
        this.uploadingError = false;
        this.uploadingErrorMessage = '';
        this.uploadingPCT = '0%';
        this.uploadImage(formData, {
          signal: this.cancellation.signal,
          onUploadProgress: function (progressEvent) {
            this.uploadingPCT =
              Math.floor((progressEvent.loaded * 100) / progressEvent.total) + '%';
            log(`${this.imageToUpload.name} => ${this.uploadingPCT}`);
          }.bind(this),
        })
          .then(
            function (response) {
              log('Uploading Image success', response, response.data);
              this.emptyMode = false;

              this.uploadingMode = false;
              this.uploadingActive = false;
              this.uploadingError = false;

              this.imageToUpload = null;
              this.fakeImageUrl = null;

              this.imageIsValid = true;
              this.activeImageUrl = response.data.url;
              this.annouceImageUpdate();
            }.bind(this),
          )
          .catch(
            function (error) {
              log('Uploading Image Error:', error, this.cancellation);

              if (this.cancellation && this.cancellation.signal.aborted) {
                //check if canceled
                log('Request canceled', error.message);
                this.deleteNewImage();
              } else {
                //handle error
                this.uploadingError = true;
                this.uploadingErrorMessage = error.response?.data?.message || error.message || 'Upload failed';
                this.uploadingActive = false;
                this.$emit('error', this.uploadingErrorMessage);
              }
            }.bind(this),
          );
      },
      uploadImage(imageData, config) {
        return httpService.post({
          url: this.apiUrl,
          data: imageData,
          headers: config,
        });
      },
    },
  };
</script>

<style lang="scss">
  .v-badge__badge .v-icon {
    font-size: 14px !important;
  }
  .siu-label {
    font-size: 15px !important;
    font-weight: 600 !important;
  }
  .single-image-uploader {
    margin-top: 4px !important;
    display: flex;
    width: 100%;
    flex-direction: column;
    .siu-dropzone {
      position: relative;
      border-radius: 8px;
      padding: 1rem 1rem;
      text-align: center;
      border: 2px dashed #ccc;
      background: transparent;
      &.uploading-error {
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='rgb(229, 76, 60, 1)' stroke-width='3' stroke-dasharray='6%2c 6' stroke-dashoffset='4' stroke-linecap='butt'/%3e%3c/svg%3e");
        background-color: rgb(229, 76, 60, 0.1);
      }

      .siu-empty-placeholder {
        .siu-icon {
          color: rgba(var(--v-black-base), 0.87);
          margin-bottom: 0.25rem;
        }

        .siu-main-info {
          color: rgba(var(--v-black-base), 0.87);
          font-weight: 600;
          font-size: 14px;
          margin: 0;

          label {
            color: var(--v-primary-base);
            font-weight: 700;
            cursor: pointer;

            &:hover {
              text-decoration: underline;
            }

            &[disabled='disabled'] {
              color: var(--v-secondary-base);
              cursor: default;
              text-decoration: none !important;
            }
          }
        }
        .siu-support {
          color: rgba(var(--v-black-base), 0.4);
          font-weight: 700;
          font-size: 12px;
          margin: 0;
          margin-top: 0.25rem;
        }
      }

      .siu-drop-catcher {
        content: 'Drop here';
        display: flex;
        -ms-flex-direction: column;
        -webkit-flex-direction: column;
        flex-direction: column;
        -webkit-justify-content: center;
        justify-content: center;
        -webkit-align-items: center;
        align-items: center;
        color: var(--v-primary-base);
        font-size: 14px;
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        z-index: 4;
        background-color: var(--v-black-base);
        -webkit-backdrop-filter: saturate(180%) blur(20px);
        backdrop-filter: saturate(180%) blur(20px);
        overflow: hidden;
        opacity: 1;
        transition: all 0.3s ease-out;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='rgba(255, 138, 0, 1)' stroke-width='4' stroke-dasharray='6%2c 6' stroke-dashoffset='4' stroke-linecap='butt'/%3e%3c/svg%3e");
        border-radius: 8px;

        span {
          font-weight: 500;
        }

        * {
          user-select: none !important;
          pointer-events: none !important;
        }
      }
    }

    .siu-queue {
      position: relative;
      border-radius: 0.5rem;

      .image-uploader {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 0.5rem;

        .image-info {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          width: calc(100% - 100px - 0.5rem);

          .image-name {
            font-weight: 700;
            font-size: 16px;
            font-size: 16px;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            text-align: left;
          }
          .image-size {
            font-weight: 600;
            font-size: 12px;
            opacity: 0.54;
          }
        }

        .image-thumb {
          width: 100px;
          height: 100px;
          object-fit: contain;
          padding: 0.5rem;
        }

        .image-actions {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          margin-top: 0.5rem;
        }
      }

      .uploading-progress {
        display: flex;
        height: 100%;
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        opacity: 1;
        pointer-events: none;
        -moz-user-select: none;
        -ms-user-select: none;
        -webkit-user-select: none;
        user-select: none;
        border-radius: 0.5rem;
        overflow: hidden;

        .bar {
          position: absolute;
          height: 100%;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          background: rgba(var(--v-black-base), 0.08);
        }

        .percentage {
          position: absolute;
          bottom: 0.5rem;
          right: 0.5rem;
          font-size: 32px;
          font-weight: 700;
          color: rgba(var(--v-black-base), 0.16);
        }
      }
    }

    .siu-result {
      position: relative;

      .image-viewer {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 0.5rem;

        .image-thumb {
          width: 100px;
          height: 100px;
          object-fit: contain;
          padding: 0.5rem;
          cursor: pointer !important;
        }
      }

      .image-actions {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 0.5rem;

        label {
          cursor: pointer;
        }
      }
    }
  }
</style>
